// -- Widget registry -------------------------------------------------------
// Each customisable page is composed of named sections. A section is a
// labelled cluster of widgets that share a job-to-be-done (snapshot, trend,
// breakdown, detail table). Pages render section titles above each cluster
//, matching the "Score drivers" pattern on Home, so the reader knows what
// question each block answers. The customise drawer mirrors the sections
// 1:1, with widgets toggled by ON/OFF switches inside each.
//
// Ordering follows the analytics-dashboard convention used by Stripe Sigma,
// Datadog, Looker and Mixpanel: KPIs / health first, then trends, then
// breakdowns, then row-level detail. A widget turned off leaves dead space
// for its siblings inside the same section to fill; sections never affect
// each other's layout.
//
// Calculations: none. This is a pure registry; consumers
// (useWidgetVisibility) derive `visibleWidgets` and `anyVisible` from the
// section definitions plus the user's hidden-id list.
//
// Postgres mapping: registry is product copy and stays in code. Per-user
// hidden-id state should move into `user_preferences` keyed by
// (user_id, 'widget_visibility:<page>'). Section templates (TEMPLATES)
// could become a tenancy-configurable presets table if customers want
// to ship branded default layouts (`tenancy_widget_templates`).
//
// New data points: none required, all widget ids reference data points
// already covered by other modules.
//
// Integration hook: serve via `useWidgetCatalogQuery` (static) and
// `useUserPrefsQuery` (per-user state). Cache: catalog infinite,
// per-user state 1 hour.

export const TEMPLATES = {
    sims: [
        { id: 'full',      label: 'Full',         description: 'All widgets',            hidden: [] },
        { id: 'compact',   label: 'Compact',      description: 'Key metrics and tables', hidden: ['heatmap-card', 'device-breakdown', 'sim-engagement', 'report-trend', 'click-by-sim', 'users-table', 'metric-flagged', 'time-to-click'] },
        { id: 'analytics', label: 'Analytics',    description: 'Charts and breakdowns',  hidden: ['all-sims', 'users-table', 'high-risk', 'send-history', 'metric-flagged'] },
        { id: 'risk',      label: 'Risk focused', description: 'Risk signals only',      hidden: ['click-by-sim', 'heatmap-card', 'device-breakdown', 'click-by-dept', 'users-table', 'send-history'] },
    ],
    courses: [
        { id: 'full',     label: 'Full',     description: 'All widgets',                      hidden: [] },
        { id: 'compact',  label: 'Compact',  description: 'KPIs, top breakdowns and actions', hidden: ['dept-by-type-heatmap', 'score-by-type-marimekko', 'score-marimekko', 'course-send-log-card', 'course-lag-chart'] },
        { id: 'quality',  label: 'Score quality', description: 'Score-distribution focused',  hidden: ['by-dept-completion', 'dept-by-type-heatmap', 'outstanding-users-card', 'course-send-log-card', 'course-lag-chart'] },
    ],
    summary: [],
};

// -- Section definitions ---------------------------------------------------
// One entry per customisable page. Each section's `widgets` array is the
// canonical render order; pages walk this order so adding, removing or
// reordering a widget happens in this one file.
export const WIDGET_SECTIONS = {
    summary: [
        {
            id: 'risk-summary',
            title: 'Risk summary',
            description: 'Where the org stands right now and how the score is moving.',
            widgets: [
                {
                    id: 'risk-dial',
                    label: 'Risk Score',
                    description: 'Overall org risk on a 0-100 scale, with band, since-last-visit delta and a link to the methodology.',
                },
                {
                    id: 'benchmark-position',
                    label: 'Industry Benchmark',
                    description: 'Where your org sits on the risk scale relative to industry average and top-quartile peers, with a percentile rank and peer distribution.',
                    feature: 'industryBenchmarking',
                },
                {
                    id: 'risk-trend',
                    label: 'Risk Score Trend',
                    description: 'Monthly risk score over the past 6 months plotted against the industry average.',
                },
                {
                    id: 'ai-summary',
                    label: 'Risk highlights',
                    description: 'Short AI-generated narrative of the most significant risk signals for the period.',
                },
            ],
        },
        {
            id: 'score-drivers',
            title: 'Score drivers',
            description: 'The five behavioural and training factors that compose the score.',
            widgets: [
                {
                    id: 'factor-strip',
                    label: 'Score drivers',
                    description: 'The five factors that make up the risk score, each shown with an industry anchor. Click any card to explore the data behind it.',
                },
            ],
        },
        {
            id: 'meta-league',
            title: 'League table',
            description: 'Top 5 meta-data values ranked by risk score.',
            widgets: [
                {
                    id: 'meta-risk-league',
                    label: 'League Table',
                    description: 'Top 5 meta-data values across the selected dimension ranked by risk score. Filter by department, location, city, country, company, state or AD group.',
                },
            ],
        },
    ],

    courses: [
        {
            id: 'performance',
            title: 'Performance',
            description: 'Headline training engagement for the period.',
            widgets: [
                { id: 'metric-courses-sent',    label: 'Courses Sent',     description: 'Total course assignments delivered in the period.' },
                { id: 'metric-completion-rate', label: 'Completion Rate',  description: 'Share of delivered courses completed in the window.' },
                { id: 'metric-avg-score',       label: 'Avg Score',        description: 'Average Score across all course attempts.' },
                { id: 'metric-pass-mark-rate',  label: 'Pass Rate',        description: 'Percentage of courses where pass mark was achieved.' },
                { id: 'metric-pass-rate',       label: 'Avg Pass Score',   description: 'Average Score where pass mark was achieved.' },
                { id: 'metric-training-debt',   label: 'Training Debt',    description: 'Count of overdue course assignments still incomplete. Backs the Training debt score driver on Home.' },
                { id: 'metric-course-lag',      label: 'Avg Course Lag',   description: 'Average days between send and completion, capped at 30. Backs the Course lag score driver on Home.' },
            ],
        },
        {
            id: 'engagement-over-time',
            title: 'Engagement over time',
            description: 'Sends and completions over the period, and how long courses are taking.',
            widgets: [
                { id: 'sends-vs-completions', label: 'Sends vs Completions', description: 'Dual-bar chart: courses sent (left) vs courses completed (right) per period.' },
                { id: 'course-lag-chart',     label: 'Course Lag',           description: 'Every completion in scope plotted by send-to-complete delay, sorted ascending so the curve climbs from fast to slow finishers. Dashed line marks the average; cap is 30 days to match the Course lag score driver on Home.' },
            ],
        },
        {
            id: 'action-needed',
            title: 'Action needed',
            description: 'Who still has training open. Top-priority view on the page.',
            widgets: [
                { id: 'outstanding-users-card', label: 'Outstanding Users', description: 'Users with overdue or incomplete assignments, sorted by lag.' },
            ],
        },
        {
            id: 'completion-breakdown',
            title: 'Completion breakdown',
            description: 'Where completion is strong and where it lags.',
            widgets: [
                { id: 'by-dept-completion',    label: 'Completion by Group',       description: 'Completion rate by department, location, country and other groupings. Pick Org for the overall roll-up.' },
                { id: 'dept-by-type-heatmap',  label: 'Department by Type Heatmap', description: 'Completion intensity across departments by course type. Click a row to scope the page. Column totals replace the standalone Completion by Type chart.' },
            ],
        },
        {
            id: 'score-quality',
            title: 'Score quality',
            description: 'How well users actually score in the assessments.',
            widgets: [
                { id: 'score-marimekko',         label: 'Score Breakdown by Course', description: 'Score-band distribution per course (column width = sends). Paginated, 6 per page.' },
                { id: 'score-by-type-marimekko', label: 'Score Breakdown by Type',   description: 'Score-band distribution aggregated by course type. Paginated, 6 per page.' },
            ],
        },
        {
            id: 'detail-tables',
            title: 'Detail tables',
            description: 'Row-level data for drill-in.',
            widgets: [
                { id: 'all-courses', label: 'All Courses', description: 'Full drillable list of courses with sends, completed and outstanding counts.' },
            ],
        },
        {
            id: 'activity-log',
            title: 'Activity log',
            description: 'Chronological feed of recent course deliveries.',
            widgets: [
                { id: 'course-send-log-card',   label: 'Course Send History',   description: 'Full-width preview of recent individual course delivery events.' },
            ],
        },
    ],

    sims: [
        {
            id: 'performance',
            title: 'Performance',
            description: 'The headline numbers for the period.',
            widgets: [
                { id: 'metric-sends',           label: 'Total Sends',     description: 'Total number of simulation emails sent in the period.' },
                { id: 'metric-click-rate',      label: 'Failure Rate',    description: 'Percentage of delivered simulations that were failed (link clicked or landing page reached). Backs the Failure rate score driver on Home.' },
                { id: 'metric-second-stage',    label: '2nd Stage Rate',  description: 'Share of sends where credentials were entered or the download link was clicked. Backs the Stage depth score driver on Home.' },
                { id: 'metric-repeat-clickers', label: 'Repeat Clickers', description: 'Users whose clicks land back-to-back: a run of two or more consecutive clicks across their tests, ordered by send date. Backs the Repeat offender score driver on Home.' },
                { id: 'metric-report-rate',     label: 'Report Rate',     description: 'Percentage of simulation emails that were reported by users.' },
                { id: 'metric-flagged',         label: 'Flagged Sims',    description: 'Number of simulations flagged as requiring attention.', feature: 'flaggedSimulations' },
            ],
        },
        {
            id: 'activity-breakdowns',
            title: 'Activity & breakdowns',
            description: 'Volume and engagement over time alongside where the clicks come from.',
            widgets: [
                { id: 'clicks-by-month',  label: 'Clicks by Date',         description: 'Email link clicks per period shown as a bar chart, with an optional dual-bar Reports comparison.' },
                { id: 'device-breakdown', label: 'Clicks by Device',       description: 'Donut showing share of clicks coming from Desktop, Mobile, Tablet and Unknown devices.' },
                { id: 'click-by-dept',    label: 'Click Rate by Group',    description: 'Horizontal bar chart of click rate by your chosen grouping, department, location, country or AD group.' },
                { id: 'click-by-sim',     label: 'Click Rate by Sim',      description: 'Treemap showing relative click volume across the top five simulations.' },
                { id: 'heatmap-card',     label: 'When Users Click',       description: 'Time-of-day click and report intensity heat grid.' },
                { id: 'sim-engagement',   label: 'Simulation Engagement',  description: 'Radial funnel showing how many users moved from receiving a sim to clicking, 2nd stage, and reporting.' },
            ],
        },
        {
            id: 'reporting',
            title: 'Click and report',
            description: 'How users engage with sims: clicks vs reports, over time and per template.',
            widgets: [
                { id: 'report-trend', label: 'Reporting Trend',  description: 'Click count vs report count over time, shows whether users are getting better at spotting phishing.' },
                { id: 'high-risk',    label: 'Click vs Report', description: 'Top simulations ranked by user engagement, click rate alongside report rate.' },
            ],
        },
        {
            id: 'lure-analysis',
            title: 'Lure analysis',
            description: 'Which psychological levers work, and how fast users fall for them.',
            widgets: [
                { id: 'lure-effectiveness', label: 'Lure Effectiveness', description: 'Tags ranked by click rate for the current scope, with sample counts so sparse buckets are visible.' },
                { id: 'time-to-click',      label: 'Time to Click',      description: 'Cumulative share of clicked sims by delivery-to-click delay. Steeper curve = faster burn through victims.' },
            ],
        },
        {
            id: 'tables',
            title: 'Detail tables',
            description: 'Row-level data for drill-in.',
            widgets: [
                { id: 'all-sims',     label: 'All Simulations', description: 'Full drillable list of simulations with risk, difficulty, click rate and tags.' },
                { id: 'users-table',  label: 'Users',           description: 'All users sorted by risk score with sim clicks and course completion.' },
                { id: 'send-history', label: 'Simulation Send History', description: 'Preview of recent individual simulation delivery events with a link to the full log.' },
            ],
        },
    ],
};

// Flat-list view of the registry, preserves the shape consumers like
// useWidgetVisibility and WidgetPanel were already built against, but now
// derived from the section structure above so render order, customise order
// and stored order are guaranteed to match.
export const WIDGET_REGISTRY = Object.fromEntries(
    Object.entries(WIDGET_SECTIONS).map(([page, sections]) => [
        page,
        sections.flatMap((section) =>
            section.widgets.map((w) => ({ ...w, section: section.id, group: section.title })),
        ),
    ]),
);

// Helpers used by useWidgetVisibility and the customise drawer.
export function getSections(page) {
    return WIDGET_SECTIONS[page] || [];
}

export function getWidgetSectionId(page, widgetId) {
    const sections = WIDGET_SECTIONS[page] || [];
    for (const s of sections) {
        if (s.widgets.some((w) => w.id === widgetId)) return s.id;
    }
    return null;
}
